import {SubAPI} from "./SubAPI";

export type TpSource = {
    id: number;
    tpCatalog: number;
    tpRawExtractionRun: number | null;
    pdfAddr: string;
    srcText: string;
    bbox: any;
    verified: boolean;
}

export type TpCatalogListItem = {
    id: number;
    manufacturer: number;
    manufacturerName: string;
    file: number;
    name: string;
    tpRawCatalog: number | null;
    rawStatus: string | null;
    pageCount: number;
    groupCount: number;
    ruleCount: number;
    createdAt: string;
}

// TpCatalogHeader is the short catalog identity shown in stage page headers.
export type TpCatalogHeader = {
    id: number;
    name: string;
    manufacturerName: string;
    tpRawCatalog: number | null;
    rawStatus: string | null;
}

export type TpOptionKind = "choice" | "flag" | "dimension";
export type TpSelectMode = "exclusive" | "multi-select";
export type TpDimKind = "user-defined" | "from-list" | "rounded-to";
export type TpRuleType = "option-pairing" | "opening-restriction" | "group-requirement" | "prep" | "unknown";
export type TpPrepKind = "template-specific" | "template-generic" | "standard-prep" | "strike-prep" | "none";
// self = the hardware itself carries the prep (mullions)
export type TpPrepTarget = "door" | "frame" | "self";
export type TpAssetKind = "cut-sheet" | "image" | "spec";

export type TpValue = {
    id: number;
    tpRawGroupOption: number;
    code: string;
    label: string;
    description: string;
    priceAdder: number | null;
    tpRawNomenclatureOptionValue: number | null;
    tpOptionValue: number | null;
    reviewedAt: string | null;
    sources: TpSource[];
}

export type TpOption = {
    id: number;
    tpRawGroup: number;
    optName: string;
    optKind: TpOptionKind;
    selectMode: TpSelectMode;
    required: boolean;
    pdfAddr: string;
    dimKind: TpDimKind | null;
    dimValues: string;
    dimInterval: string;
    dimMin: string;
    dimMax: string;
    dimUnit: string;
    tpRawNomenclatureOption: number | null;
    tpOption: number | null;
    reviewedAt: string | null;
    sources: TpSource[];
    values: TpValue[];
}

export type TpAsset = {
    id: number;
    tpRawGroup: number;
    kind: TpAssetKind;
    pdfAddr: string;
}

export type TpGroup = {
    id: number;
    tpRawCatalog: number;
    code: string;
    name: string;
    description: string;
    isSeries: boolean;
    pdfAddr: string;
    productCategory: number | null;
    productCategoryName: string | null;
    // the template for the whole group; per-configuration templates are prep rules
    tpTmplDoc: number | null;
    tpTmplDocEntry: number | null;
    reviewedAt: string | null;
    sources: TpSource[];
    options: TpOption[];
    assets: TpAsset[];
    ruleIds: number[];
    optionRules: TpGroupOptionRule[];
    categoryMatches: TpGroupCategoryMatch[];
}

export type TpRuleOption = {
    id: number;
    tpRawRule: number;
    tpRawGroupOption: number;
    tpRawGroupOptionValue: number | null;
}

export type TpRule = {
    id: number;
    tpRawCatalog: number;
    name: string;
    ruleType: TpRuleType;
    statement: string;
    // prep* and tpTmplDoc* are set only when ruleType = prep
    prepKind: TpPrepKind | null;
    prepTarget: TpPrepTarget | null;
    prepTemplateNo: string;
    prepCode: string;
    tpTmplDoc: number | null;
    tpTmplDocEntry: number | null;
    reviewedAt: string | null;
    sources: TpSource[];
    groupIds: number[];
    options: TpRuleOption[];
}

export type TpOptionRelation = "excludes" | "requires";

// TpGroupOptionRule is a structured constraint between two options / values of one group.
// subjectValue / targetValue null = the whole option.
export type TpGroupOptionRule = {
    id: number;
    tpRawGroup: number;
    tpRawExtractionRun: number | null;
    tpRawRule: number | null;
    relation: TpOptionRelation;
    subjectOption: number;
    subjectValue: number | null;
    targetOption: number;
    targetValue: number | null;
    statement: string;
    reviewedAt: string | null;
    archived: boolean;
}

// TpRuleGroup is one rule -> group link. reviewedAt records that the rule applies to the group.
export type TpRuleGroup = {
    id: number;
    tpRawRule: number;
    tpRawGroup: number;
    reviewedAt: string | null;
}

export type TpFinish = {
    id: number;
    code: string;
    name: string;
    sources: TpSource[];
}

export type TpNomenclatureValue = {
    id: number;
    tpRawNomenclatureOption: number;
    code: string;
    label: string;
    description: string;
    sources: TpSource[];
}

export type TpNomenclatureOption = {
    id: number;
    tpRawNomenclature: number;
    key: string;
    name: string;
    description: string;
    sources: TpSource[];
    values: TpNomenclatureValue[];
}

export type TpNomenclature = {
    id: number;
    tpRawCatalog: number;
    name: string;
    format: string;
    pdfAddr: string;
    sources: TpSource[];
    groupIds: number[];
    options: TpNomenclatureOption[];
}

export type TpCategoryItem = {
    id: number;
    name: string;
    parentName: string | null;
}

export type TpGroupCategoryMatchOption = {
    id: number;
    tpRawGroupProductCategoryMatch: number;
    tpRawGroupOption: number;
    tpRawGroupOptionValue: number | null;
}

export type TpGroupCategoryMatch = {
    id: number;
    tpRawGroup: number;
    productCategory: number;
    productCategoryName: string;
    tpRawExtractionRun: number | null;
    reviewedAt: string | null;
    archived: boolean;
    options: TpGroupCategoryMatchOption[];
}

export type TpStandardValue = {
    id: number;
    tpOption: number;
    code: string;
    label: string;
    // the standalone option a feature-set value turns on; null = plain feature
    featureOption: number | null;
}

export type TpStandardOption = {
    id: number;
    productCategory: number | null;
    name: string;
    // place in the property order (type and function first, finish and handing last); null = not placed
    sortOrder: number | null;
    // a bag of independent features; the lookup shows each value as an on / off feature
    featureSet: boolean;
    // how the lookup presents the option; null = as the raw options that link to it
    kind: TpOptionKind | null;
    values: TpStandardValue[];
}

export type TpCatalogDetail = {
    catalog: TpCatalogListItem;
    finishes: TpFinish[];
    groups: TpGroup[];
    rules: TpRule[];
    ruleGroups: TpRuleGroup[];
    nomenclatures: TpNomenclature[];
    standardOptions: TpStandardOption[];
    categories: TpCategoryItem[];
}

export type TpLookupCatalog = {
    id: number;
    tpRawCatalog: number;
    name: string;
    manufacturer: number;
    manufacturerName: string;
}

export type TpLookupNomenclature = {
    id: number;
    tpRawCatalog: number;
    name: string;
    format: string;
    pdfAddr: string;
    groupIds: number[];
    options: { id: number; tpRawNomenclature: number; key: string; name: string }[];
}

// TpLookupPrepRule is a prep rule of a result group. options are the conditions that pick the
// configurations it covers; none = the whole group.
export type TpLookupPrepRule = Omit<TpRule, "sources">;

export type TpLookupTmplDoc = {
    id: number;
    title: string;
    templateNo: string;
    docNo: string;
    pageCount: number;
}

// TpLookupResult is every group whose category (or a category match override) is inside the
// requested category subtree, across all catalogs.
export type TpLookupResult = {
    categoryIds: number[];
    catalogs: TpLookupCatalog[];
    groups: TpGroup[];
    nomenclatures: TpLookupNomenclature[];
    standardOptions: TpStandardOption[];
    prepRules: TpLookupPrepRule[];
    tmplDocs: TpLookupTmplDoc[];
}

export class TpLibrary extends SubAPI {
    lookup(props: { productCategory: number }) {
        return this.fetcher.get<TpLookupResult>("/api/tp-library/lookup", props);
    }


    listCatalogs(props: {}) {
        return this.fetcher.get<TpCatalogListItem[]>("/api/tp-library/catalogs", props);
    }

    getCatalog(props: { id: number }) {
        return this.fetcher.get<TpCatalogDetail>("/api/tp-library/catalog", props);
    }

    pdfUrl(props: { id: number }) {
        return "/api/tp-library/catalog/pdf?id=" + props.id;
    }

    // reviewed = true also marks the group's options, values and rule links
    reviewGroup(props: { id: number; reviewed: boolean }) {
        return this.fetcher.post("/api/tp-library/raw/group/review", props);
    }

    // reviewed = true also marks the option's values
    reviewGroupOption(props: { id: number; reviewed: boolean }) {
        return this.fetcher.post("/api/tp-library/raw/group-option/review", props);
    }

    reviewGroupOptionValue(props: { id: number; reviewed: boolean }) {
        return this.fetcher.post("/api/tp-library/raw/group-option-value/review", props);
    }

    // the rule was parsed correctly (one mark per rule)
    reviewRule(props: { id: number; reviewed: boolean }) {
        return this.fetcher.post("/api/tp-library/raw/rule/review", props);
    }

    // the rule applies to the group (id = TpRuleGroup.id)
    reviewRuleGroup(props: { id: number; reviewed: boolean }) {
        return this.fetcher.post("/api/tp-library/raw/rule-group/review", props);
    }

    // the structured option rule is correct
    reviewGroupOptionRule(props: { id: number; reviewed: boolean }) {
        return this.fetcher.post("/api/tp-library/raw/group-option-rule/review", props);
    }

    // id = 0 adds a user-written rule; otherwise only archived is updated
    upsertGroupOptionRule(props: TpGroupOptionRule) {
        return this.fetcher.post<{ id: number }>("/api/tp-library/raw/group-option-rule", props);
    }

    // sets the group's default product category (null clears it)
    upsertGroupCategory(props: { id: number; productCategory: number | null }) {
        return this.fetcher.post("/api/tp-library/raw/group/category", props);
    }

    reviewGroupCategoryMatch(props: { id: number; reviewed: boolean }) {
        return this.fetcher.post("/api/tp-library/raw/group-category-match/review", props);
    }

    // id = 0 adds a user-written match with its conditions; otherwise only archived is updated
    upsertGroupCategoryMatch(props: Omit<TpGroupCategoryMatch, "productCategoryName" | "tpRawExtractionRun" | "reviewedAt">) {
        return this.fetcher.post<{ id: number }>("/api/tp-library/raw/group-category-match", props);
    }
}
